import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback } from 'react';

import { CheckboxDumb } from '@webapp/common/components/checkbox-dumb';
import { GradientNumbers } from '@webapp/common/components/gradient-numbers';
import { NumbersWithBorders } from '@webapp/common/components/numbers-with-borders';
import { Slider } from '@webapp/common/components/slider';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';

import { LeftRight } from '../left-right';

import css from './nps.css';

export const Nps: VFC<SurveyQuestion> = ({
    actor,
    question: {
        id: questionId,
        params: { npsColors, npsLeftLabel, npsRightLabel, cantRate, cantRateText, npsType },
        responseActor,
    },
}) => {
    const { value } = useSelector(responseActor, (r) => r.context);
    const cantRateChecked = value === CANTRATE_ID;

    const handleChecked = useCallback(
        (checked: boolean) => {
            const nextValue = checked ? CANTRATE_ID : '';
            responseActor.send({ type: 'CHANGE', value: nextValue, questionId });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', value: nextValue, questionId });
        },
        [actor, questionId, responseActor],
    );

    const handleSelect = useCallback(
        (value: number) => {
            responseActor.send({ type: 'CHANGE', value, questionId });
            // FIXME: handle RESPONSE_CHANGE in question-machine
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', value, questionId });
        },
        [actor, questionId, responseActor],
    );

    const renderInput = (val?: number) => {
        if (npsType === 'numbers-with-borders') {
            return <NumbersWithBorders colors={npsColors} startsWith={0} selectedValue={val} onClick={handleSelect} />;
        }
        if (npsType === 'slider') {
            return (
                <Slider
                    min={0}
                    max={10}
                    value={typeof val === 'number' ? val : null}
                    withoutControl
                    rangeClassName={css.rangeSlider}
                    labelsBottom
                    trackBgImage={`linear-gradient(90deg, ${npsColors.join(', ')})`}
                    onChange={handleSelect}
                />
            );
        }

        return (
            <GradientNumbers
                size='small'
                colors={npsColors}
                itemsCount={11}
                startsWith={0}
                selectedValue={val}
                onClick={handleSelect}
            />
        );
    };

    return (
        <div className={css.container}>
            <LeftRight className={cn(CssQuestionType.NPS)} left={npsLeftLabel} right={npsRightLabel}>
                {renderInput(cantRateChecked ? undefined : (value as number))}
            </LeftRight>
            {cantRate && (
                <div
                    style={{
                        display: 'flex',
                        placeContent: 'baseline',
                    }}
                >
                    <CheckboxDumb
                        checked={cantRateChecked}
                        className={css.cantRateCheckbox}
                        label={cantRateText || PLACEHOLDER.CANT_RATE}
                        round={false}
                        onChange={handleChecked}
                    />
                </div>
            )}
        </div>
    );
};
Nps.displayName = 'Nps';
